import { Price } from '../models/serializable/price.js';
import { EventTicketOrder } from '../models/serializable/event-ticket-order.js';
import { EventTicket, EventTicketStatus } from '../models/serializable/event-ticket.js';
import { DiscountHelper } from '../payment/discount-helper.js';
import { isNotNullOrUndefined, isNullOrUndefined } from './null-safety.js';
import { TicketType } from '../models/serializable/ticket-type.js';
import { OnlineEventTicketOrder } from '../models/serializable/online-event-ticket-order.js';
import { cloneData, cloneMap } from '../models/dto/data-model-utils.js';
import { ICloneable } from '../contracts/cloneable.js';
import { LegacyPaymentHelper } from '../payment/legacy-payment-helper.js';

const LOUPE_ORGANIZATION_ID : string = 'rfkpMi0IXRv4WBW9AHs9XarfG65N';
const LOUPE_PASS_PRICE_PER_TICKET : number = 533333;

export const GIFT_CARD_ABBR : string = 'GC-';
export const OPEN_PASS_ABBR : string = 'OP-';
export const SEASON_PASS_ABBR : string = 'SP-';
export const OPEN_OR_SEASON_PASS_ABBR : string = 'OP-SP';
export const PROTOCOL_TICKET_ABBR : string = 'PT';

export class DiscountDetails implements ICloneable {
  constructor(
        public originalPrice: Price,
        public discountedPrice: Price,
        public discountType: string,
  ) {}

  clone() : this {
    return new DiscountDetails(
        this.originalPrice.clone(),
        this.discountedPrice.clone(),
        this.discountType,
    ) as this;
  }
}

export class OrderDiscountDetails implements ICloneable {
  constructor(
      public orderDetails : DiscountDetails,
      public ticketsDetails : Map<string, DiscountDetails>,
  ) {}

  add(other: OrderDiscountDetails) : OrderDiscountDetails | null {
    const clone = this.clone();
    const currency = this.orderDetails.originalPrice.currency;

    if(other.orderDetails.originalPrice.currency !== currency) {
      return null;
    }

    clone.orderDetails.originalPrice = clone.orderDetails.originalPrice.add(other.orderDetails.originalPrice);

    if(other.orderDetails.discountedPrice.currency !== currency) {
      return null;
    }

    clone.orderDetails.discountedPrice = clone.orderDetails.discountedPrice.add(other.orderDetails.discountedPrice);

    for(const ticketsDetails of other.ticketsDetails.entries()) {
      if(ticketsDetails[1].originalPrice.currency !== currency || ticketsDetails[1].discountedPrice.currency !== currency) {
        return null;
      }

      clone.ticketsDetails.set(ticketsDetails[0], ticketsDetails[1]);
    }

    return clone;
  }

  clone() : this {
    return new OrderDiscountDetails(
        this.orderDetails.clone(),
        cloneMap(this.ticketsDetails, (e) => cloneData(e)),
    ) as this;
  }
}

// This must be always synced with discount_utils.dart (in-time-business-web)
// Except the online ticket order related part, because we do not use that in the other project yet.
// Use only for statistics.
// We assume the following statements are true
//  - We cannot have discount on the order and on the tickets in the same time
//  - Every ticket of an order has the same type
export class StatisticsDiscountUtils {
  static getOrderDiscountDetailsOfOnlineTicketOrder(order: OnlineEventTicketOrder) : OrderDiscountDetails | null {
    if(order.tickets.length === 0) {
      return null;
    }

    const currency = order.tickets[0].template.price.currency;
    let result : OrderDiscountDetails | null = new OrderDiscountDetails(
        new DiscountDetails(
            Price.zero(currency),
            Price.zero(currency),
            '',
        ),
        new Map<string, DiscountDetails>(),
    );

    const ticketOrder = EventTicketOrder.fromOnlineOrder(order);
    const discountDetails = this.getOrderDiscountDetailsOfTicketOrder(ticketOrder, false);
    if(isNullOrUndefined(discountDetails)) {
      return null;
    }

    result = result.add(discountDetails);

    return result;
  }

  static getOrderDiscountDetailsOfTicketOrder(order: EventTicketOrder, canRefundPartially: boolean = true) : OrderDiscountDetails | null {
    if(order.tickets.length === 0) {
      return null;
    }

    switch(order.tickets[0].ticketType) {
      case TicketType.Standard:
        return this.generateStandardOrderDiscountDetails(order, canRefundPartially);
      case TicketType.Protocol:
        return this.generateProtocolOrderDiscountDetails(order);
      default:
        return this.generateEmptyOrderDiscountDetails(order, canRefundPartially);
    }
  }

  private static generateStandardOrderDiscountDetails(order: EventTicketOrder, canRefundPartially: boolean = true) : OrderDiscountDetails | null {
    const currency = order.tickets[0].template.price.currency;
    const result = new OrderDiscountDetails(
        new DiscountDetails(
            Price.zero(currency),
            Price.zero(currency),
            '',
        ),
        new Map<string, DiscountDetails>(),
    );

    let isLoupePass : boolean = false;
    for(const ticket of order.tickets) {
      if(ticket.template.price.currency !== currency) {
        return null;
      }

      const isOpenPass = ticket.discount?.code?.startsWith(OPEN_PASS_ABBR) ?? false;
      const isSeasonPass = ticket.discount?.code?.startsWith(SEASON_PASS_ABBR) ?? false;
      const isOpenOrSeasonPass = isOpenPass || isSeasonPass;
      const isLoupe = ticket.businessRef.organizationId === LOUPE_ORGANIZATION_ID;
      if(isOpenOrSeasonPass && isLoupe) {
        isLoupePass = true;
      }

      result.ticketsDetails.set(ticket.uniqueId, new DiscountDetails(
          ticket.template.price.clone(),
          ticket.template.price.clone(),
          '',
      ));
    }

    if(isLoupePass) {
      // HACK BECAUSE WE DON'T KNOW THE PRICE OF A PASS!!! REEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEE
      return this.generateLoupePassOrderDiscountDetails(order, result);
    }

    if(isNotNullOrUndefined(order.discount)) {
      result.orderDetails.discountType = this.getDiscountTypeOfOrder(order);

      const originalOrderPrice = LegacyPaymentHelper.calculateIncomeWithoutDiscountForOrder(
          order,
          { applyProducts: false, applySupplements: true }
      );

      const discountedOrderPrice = LegacyPaymentHelper.calculateIncomeForOrder(
          order,
          { applyProducts: false, applySupplements: true }
      );

      if(isNullOrUndefined(originalOrderPrice) || isNullOrUndefined(discountedOrderPrice)) {
        return null;
      }

      result.orderDetails.originalPrice = originalOrderPrice.clone();
      result.orderDetails.discountedPrice = discountedOrderPrice.clone();

      if(discountedOrderPrice.isSame(originalOrderPrice)) {
        return result;
      }

      const discountPercentage = discountedOrderPrice.value * 100 / originalOrderPrice.value;
      for(const entry of result.ticketsDetails.entries()) {
        const discountedPrice = DiscountHelper.applyPercentageDiscountToPrice(entry[1].originalPrice, discountPercentage);
        result.ticketsDetails.get(entry[0])!.discountedPrice = discountedPrice;
        result.ticketsDetails.get(entry[0])!.discountType = this.getDiscountTypeOfOrder(order);
      }

      let totalPrice = Price.zero(currency);
      for(const value of result.ticketsDetails.values()) {
        totalPrice = totalPrice.add(value.discountedPrice);
      }

      let diff : Price = discountedOrderPrice.difference(totalPrice);

      for(const entry of result.ticketsDetails.entries()) {
        if(diff.value === 0) {
          break;
        }

        let value : Price = entry[1].discountedPrice.add(diff);
        if(value.isNegative()) {
          diff = value.clone();
          value = Price.zero(currency);
        }
        else {
          diff = Price.zero(currency);
        }

        result.ticketsDetails.get(entry[0])!.discountedPrice = value.clone();
      }
    }
    else {
      const originalOrderPrice = LegacyPaymentHelper.calculateIncomeWithoutDiscountForOrder(
          order,
          { applyProducts: false, applySupplements: true }
      );

      if(isNullOrUndefined(originalOrderPrice)) {
        return null;
      }

      let discountedOrderPrice : Price = Price.zero(currency);
      for(const ticket of order.tickets) {
        const discountedTicketPrice = LegacyPaymentHelper.calculateIncomeForEventTicket(ticket, { applySupplement: true });
        if(isNullOrUndefined(discountedTicketPrice)) {
          return null;
        }

        result.ticketsDetails.get(ticket.uniqueId)!.discountedPrice = discountedTicketPrice;
        result.ticketsDetails.get(ticket.uniqueId)!.discountType = this.getDiscountTypeOfTicket(ticket);
        discountedOrderPrice = discountedOrderPrice.add(discountedTicketPrice);
      }

      result.orderDetails.originalPrice = originalOrderPrice;
      result.orderDetails.discountedPrice = discountedOrderPrice;
    }

    if(canRefundPartially) {
      // Hack: attempt to handle refunded tickets.
      for(const ticket of order.tickets) {
        if(ticket.status !== EventTicketStatus.Live && ticket.status !== EventTicketStatus.Scanned && ticket.status !== EventTicketStatus.Canceled) {
          result.orderDetails.originalPrice = result.orderDetails.originalPrice.difference(result.ticketsDetails.get(ticket.uniqueId)!.originalPrice);
          result.orderDetails.discountedPrice = result.orderDetails.discountedPrice.difference(result.ticketsDetails.get(ticket.uniqueId)!.discountedPrice);
          result.ticketsDetails.delete(ticket.uniqueId);
        }
      }
    }

    return result;
  }

  private static generateProtocolOrderDiscountDetails(order : EventTicketOrder) : OrderDiscountDetails | null {
    const currency = order.tickets[0].template.price.currency;
    const result = new OrderDiscountDetails(
        new DiscountDetails(
            Price.zero(currency),
            Price.zero(currency),
            PROTOCOL_TICKET_ABBR,
        ),
        new Map<string, DiscountDetails>(),
    );

    for(const ticket of order.tickets) {
      result.ticketsDetails.set(ticket.uniqueId, new DiscountDetails(
          Price.zero(currency),
          Price.zero(currency),
          PROTOCOL_TICKET_ABBR,
      ));
    }

    return result;
  }

  private static generateEmptyOrderDiscountDetails(order : EventTicketOrder, canRefundPartially: boolean = true) : OrderDiscountDetails | null {
    const currency = order.tickets[0].template.price.currency;
    const result = new OrderDiscountDetails(
        new DiscountDetails(
            Price.zero(currency),
            Price.zero(currency),
            '',
        ),
        new Map<string, DiscountDetails>(),
    );

    for(const ticket of order.tickets) {
      if(ticket.template.price.currency !== currency) {
        return null;
      }

      result.ticketsDetails.set(ticket.uniqueId, new DiscountDetails(
          ticket.template.price.clone(),
          ticket.template.price.clone(),
          '',
      ));
    }

    const orderPrice = LegacyPaymentHelper.calculateIncomeWithoutDiscountForOrder(
        order,
        { applyProducts: false, applySupplements: true }
    );

    if(isNullOrUndefined(orderPrice)) {
      return null;
    }

    result.orderDetails.originalPrice = orderPrice.clone();
    result.orderDetails.discountedPrice = orderPrice.clone();

    if(canRefundPartially) {
      // Hack: attempt to handle refunded tickets.
      for(const ticket of order.tickets) {
        if(ticket.status !== EventTicketStatus.Live && ticket.status !== EventTicketStatus.Scanned && ticket.status !== EventTicketStatus.Refunded) {
          result.orderDetails.originalPrice = result.orderDetails.originalPrice.difference(result.ticketsDetails.get(ticket.uniqueId)!.originalPrice);
          result.orderDetails.discountedPrice = result.orderDetails.discountedPrice.difference(result.ticketsDetails.get(ticket.uniqueId)!.discountedPrice);
          result.ticketsDetails.delete(ticket.uniqueId);
        }
      }
    }

    return result;
  }

  private static generateLoupePassOrderDiscountDetails(order : EventTicketOrder, result : OrderDiscountDetails) : OrderDiscountDetails | null {
    const orderPrice = LegacyPaymentHelper.calculateIncomeWithoutDiscountForOrder(
        order,
        { applyProducts: false, applySupplements: true }
    );

    if(isNullOrUndefined(orderPrice)) {
      return null;
    }

    const currency = order.tickets[0].template.price.currency;

    result.orderDetails = new DiscountDetails(
        orderPrice.clone(),
        Price.zero(currency),
        OPEN_OR_SEASON_PASS_ABBR,
    );

    for(const ticket of order.tickets) {
      if(isNotNullOrUndefined(ticket.discount)) {
        result.ticketsDetails.set(ticket.uniqueId, new DiscountDetails(
            ticket.template.price.clone(),
            ticket.discount.voucherValue?.clone() ?? Price.withCurrencyFromUnits(currency, LOUPE_PASS_PRICE_PER_TICKET),
            OPEN_OR_SEASON_PASS_ABBR,
        ));

        result.orderDetails.discountedPrice = result.orderDetails.discountedPrice.add(result.ticketsDetails.get(ticket.uniqueId)!.discountedPrice);
      }
      else {
        result.ticketsDetails.set(ticket.uniqueId, new DiscountDetails(
            ticket.template.price.clone(),
            ticket.template.price.clone(),
            '',
        ));

        result.orderDetails.discountedPrice = result.orderDetails.discountedPrice.add(result.ticketsDetails.get(ticket.uniqueId)!.originalPrice);
      }
    }

    return result;
  }

  private static getDiscountTypeOfTicket(ticket : EventTicket) : string {
    if(ticket.ticketType === TicketType.Protocol) {
      return PROTOCOL_TICKET_ABBR;
    }

    if(isNullOrUndefined(ticket.discount)) {
      return '';
    }

    const code = ticket.discount.code;
    if(isNullOrUndefined(code)) {
      return '';
    }

    if(code.startsWith(OPEN_PASS_ABBR) || code.startsWith(SEASON_PASS_ABBR)) {
      return OPEN_OR_SEASON_PASS_ABBR;
    }

    if(code.startsWith(GIFT_CARD_ABBR)) {
      return GIFT_CARD_ABBR;
    }

    return code;
  }

  private static getDiscountTypeOfOrder(order : EventTicketOrder) : string {
    if(order.tickets[0].ticketType === TicketType.Protocol) {
      return PROTOCOL_TICKET_ABBR;
    }

    if(isNullOrUndefined(order.discount)) {
      return '';
    }

    const code = order.discount.code;
    if(isNullOrUndefined(code)) {
      return '';
    }

    if(code.startsWith(OPEN_PASS_ABBR) || code.startsWith(SEASON_PASS_ABBR)) {
      return OPEN_OR_SEASON_PASS_ABBR;
    }

    if(code.startsWith(GIFT_CARD_ABBR)) {
      return GIFT_CARD_ABBR;
    }

    return code;
  }
}
